import NextLink, { type LinkProps as NextLinkPropsType } from 'next/link'
import type React from 'react'
import type { MasterData } from 'types/masterData'

export type LinkProps = Omit<
	React.AnchorHTMLAttributes<HTMLAnchorElement>,
	keyof NextLinkPropsType
> &
	React.RefAttributes<HTMLAnchorElement> & {
		prefetch?: false
		withLegacyLocale?: boolean
		className?: string
		'data-testid'?: string
		rel?: string
		target?: string
		small?: boolean
	}

interface LinkGlobalProps {
	children: React.ReactNode
	href: string
	masterData: MasterData
	id?: string
	'data-testid'?: string
	className?: string
	linkProps?: LinkProps
	onClick?: (event: React.MouseEvent) => void
	isLocalizedPath?: boolean
	logId?: string
}

export interface NextLinkProps extends LinkGlobalProps {
	prefetch?: boolean
	withLegacyLocale?: false
	skipTabKey?: boolean
	itemProp?: string
	isNextLink?: boolean
}

export interface AnchorLinkProps extends LinkGlobalProps {
	prefetch?: false
	withLegacyLocale?: boolean
	skipTabKey?: boolean
	itemProp?: string
	isNextLink?: boolean
}

const REGEX_EXTERNAL_LINK = /^(https?:\/\/|mailto:|tel:)/

export function isValidInternalLinkHref(href: string) {
	if (!REGEX_EXTERNAL_LINK.test(href) && href && !href.startsWith('/')) {
		return false
	}
	return true
}

function getLegacyLocale(
	isDefaultLanguage: boolean,
	languageISO: string,
	mangoISO: string
) {
	const lowerMangoISO = mangoISO.toLocaleLowerCase()
	return isDefaultLanguage ? lowerMangoISO : `${lowerMangoISO}-${languageISO}`
}

export function Link({
	id,
	'data-testid': dataTestId,
	children,
	href,
	masterData,
	withLegacyLocale,
	linkProps = {},
	className = '',
	onClick,
	prefetch = false,
	isLocalizedPath = false,
	skipTabKey,
	isNextLink = false,
}: NextLinkProps | AnchorLinkProps) {
	const {
		country: { languageISO, countryISO, mangoISO, isDefaultLanguage },
	} = masterData

	const external = REGEX_EXTERNAL_LINK.test(href)
	const lowerCountryISO = countryISO.toLocaleLowerCase()
	const legacyLocale = getLegacyLocale(isDefaultLanguage, languageISO, mangoISO)

	if (external || withLegacyLocale) {
		return (
			<a
				id={id}
				className={className}
				href={external ? href : `/${legacyLocale}${href}`}
				onClick={onClick}
				data-testid={dataTestId}
				tabIndex={skipTabKey ? -1 : undefined}
				aria-hidden={skipTabKey ? true : undefined}
				{...linkProps}
			>
				{children}
			</a>
		)
	}

	const trimmedHref = href === '/' ? '' : href
	const hrefLink = isLocalizedPath
		? href
		: `/${lowerCountryISO}/${languageISO}${trimmedHref}`

	if (isNextLink) {
		return (
			<NextLink
				id={id}
				prefetch={prefetch}
				className={className}
				href={hrefLink}
				onClick={onClick}
				data-testid={dataTestId}
				{...linkProps}
			>
				{children}
			</NextLink>
		)
	}

	return (
		<a
			id={id}
			className={className}
			href={hrefLink}
			onClick={onClick}
			data-testid={dataTestId}
			{...linkProps}
		>
			{children}
		</a>
	)
}
